import {
  Button,
  Grid,
  FormControlLabel,
  Checkbox,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
} from '@mui/material'
import ExpandMore from '@mui/icons-material/ExpandMore'
import EmrSectionsEditorDialog from 'src/mentor/components/PrescriptionMakingPage/components/EmrSectionsEditorDialog'
import useEmrSectionsEditorDialog from 'src/mentor/components/PrescriptionMakingPage/screeningReportViewer/hooks/useEmrSectionsEditorDialog'
import PrescriptionTemplateBodySectionsStylesOverrideHandler from './PrescriptionTemplateBodySectionsStylesOverrideHandler'

export default function PrescriptionBodySection({
  prescriptionTemplate,
  prescriptionTemplateHandlers,
  availableEmrSections = [],
  mentorProfileData,
  refreshEmrSections,
  emrSectionsApiSignature,
  emrSettingsSaveApiSignature,
  disabled = false,
}) {
  const {
    open: emrSectionsEditorDialogOpen,
    handleOpen: emrSectionsEditorDialogHandleOpen,
    handleClose: emrSectionsEditorDialogHandleClose,
  } = useEmrSectionsEditorDialog()

  const { handleBodyBooleanChange, createSetOverrideData } = prescriptionTemplateHandlers

  // Boolean field configurations
  const booleanFields = [
    {
      id: 'hideEmptyDrugAdviceColumns',
      label: 'Hide Empty Drug Advice Columns',
      value: prescriptionTemplate?.body?.hideEmptyDrugAdviceColumns || false,
    },
    {
      id: 'showPrescriptionQR',
      label: 'Show Prescription QR Code In Next Visit Column',
      value: prescriptionTemplate?.body?.showPrescriptionQR !== false, // Default true
    },
  ]

  return (
    <>
      {/* EMR Sections Management */}
      <Grid item xs={12}>
        <Button
          variant="outlined"
          onClick={emrSectionsEditorDialogHandleOpen}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            borderColor: 'primary.main',
            '&:hover': { borderColor: 'primary.dark' },
          }}
        >
          Edit EMR Sections
        </Button>
      </Grid>

      {/* Boolean Fields Section */}
      <Grid item xs={12}>
        <Typography variant="subtitle2" fontWeight={600} color="text.primary" gutterBottom>
          Body configuration
        </Typography>
      </Grid>

      {booleanFields.map((field) => (
        <Grid item xs={12} key={field.id}>
          <FormControlLabel
            control={
              <Checkbox
                id={field.id}
                checked={field.value}
                onChange={handleBodyBooleanChange}
                disabled={disabled}
              />
            }
            label={field.label}
          />
        </Grid>
      ))}

      {/* EMR Section Style Overrides */}
      <Grid item xs={12}>
        <Typography
          variant="subtitle2"
          fontWeight={600}
          color="text.primary"
          gutterBottom
          sx={{ mt: 2 }}
        >
          EMR section style overrides
        </Typography>
      </Grid>

      {availableEmrSections.map((emrSection) => {
        return (
          <Grid item xs={12} key={emrSection.id}>
            <Accordion
              slotProps={{ transition: { unmountOnExit: true } }}
              sx={{
                borderRadius: '10px !important',
                border: '1px solid',
                borderColor: 'divider',
                '&:before': { display: 'none' },
                '& + &': { mt: 1.5 },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMore />}
                sx={{ '& .MuiAccordionSummary-content': { alignItems: 'center' } }}
              >
                <Typography variant="body2" fontWeight={500}>
                  {emrSection.title || emrSection.id}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <PrescriptionTemplateBodySectionsStylesOverrideHandler
                  emrSectionId={emrSection?.id}
                  setPrescriptionTemplate={prescriptionTemplateHandlers.setPrescriptionTemplate}
                  overrideData={
                    prescriptionTemplate?.body?.styleOverridesForPrintedEmrSections?.[
                      emrSection.id
                    ] || {}
                  }
                  setOverrideData={createSetOverrideData(emrSection.id)}
                  emrSectionName={emrSection.title || emrSection.id}
                  disabled={disabled}
                />
              </AccordionDetails>
            </Accordion>
          </Grid>
        )
      })}

      {/* EMR Sections Editor Dialog */}
      <EmrSectionsEditorDialog
        hideReordering
        open={emrSectionsEditorDialogOpen}
        handleClose={emrSectionsEditorDialogHandleClose}
        refreshEmrSections={refreshEmrSections}
        mentorProfileData={mentorProfileData}
        emrSectionsApiSignature={emrSectionsApiSignature}
        emrSettingsSaveApiSignature={emrSettingsSaveApiSignature}
      />
    </>
  )
}
